import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Phone, MessageSquare, Clock, Truck, Mail } from "lucide-react";
import api from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const OPEN_STATES = ["pending", "claimed", "in_progress", "snoozed"];

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get("/work-items?item_type=cadence_call&limit=200").then((r) => r.data.items || []).catch(() => []),
      api.get("/work-items?item_type=lead_reply&limit=200").then((r) => r.data.items || []).catch(() => []),
      api.get("/work-items?item_type=sourced_outreach&limit=200").then((r) => r.data.items || []).catch(() => []),
      api.get("/work-items?item_type=mail_status&limit=200").then((r) => r.data.items || []).catch(() => []),
      api.get("/contact-submissions").then((r) => r.data.items || []).catch(() => []),
    ]).then(([calls, replies, sourced, mail, contacts]) => {
      const open = (arr, kind) =>
        arr.filter((w) => OPEN_STATES.includes(w.status)).map((w) => ({ ...w, kind }));
      const consent = sourced
        .filter((w) => OPEN_STATES.includes(w.status) && w.consent_overdue)
        .map((w) => ({ ...w, kind: "consent" }));
      const inquiries = contacts.filter((c) => !c.read).map((c) => ({
        id: c.id, kind: "inquiry", available_at: c.created_at,
        title: `New inquiry from ${c.name}${c.partnership_type ? ` · ${c.partnership_type}` : ""}`,
        description: c.message,
      }));
      const merged = [...inquiries, ...open(replies, "reply"), ...open(mail, "mail"), ...consent, ...open(calls, "call")];
      const rank = { inquiry: 0, reply: 1, mail: 2, consent: 3, call: 4 };
      merged.sort((a, b) => {
        if (a.kind !== b.kind) return rank[a.kind] - rank[b.kind];
        if (a.kind === "inquiry") return (b.available_at || "").localeCompare(a.available_at || "");
        return (a.available_at || "").localeCompare(b.available_at || "");
      });
      setItems(merged);
    });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const count = items.length;
  const replyCount = items.filter((i) => i.kind === "reply").length;

  const open = (w) => {
    if (w.kind === "inquiry") {
      api.patch(`/contact-submissions/${w.id}`, { read: true }).catch(() => {});
      setItems((prev) => prev.filter((x) => x.id !== w.id));
      navigate("/contact-requests");
    }
    else if (w.kind === "reply") navigate(`/leads/${w.entity_id}/conversation`);
    else if (w.kind === "consent") navigate(`/record/leads/${w.entity_id}`);
    else if (w.kind === "mail") {
      api.post(`/work-items/${w.id}/complete`, { outcome: "acknowledged" }).catch(() => {});
      setItems((prev) => prev.filter((x) => x.id !== w.id));
      const res = (w.payload && w.payload.resource) || w.entity_type || "leads";
      navigate(`/record/${res}/${w.entity_id}/mailer`);
    }
    else navigate(`/leads/${w.entity_id}/cadence`);
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="notification-bell-btn" title="Notifications">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-bell-count">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold">Notifications</span>
          <span className="text-[11px] text-muted-foreground">
            {replyCount > 0 ? `${replyCount} repl${replyCount === 1 ? "y" : "ies"} · ` : ""}{count} total
          </span>
        </div>
        {count === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">
            You're all caught up — no inquiries, replies or calls due.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto slim-scroll">
            {items.map((w) => {
              const isReply = w.kind === "reply";
              const isConsent = w.kind === "consent";
              const isMail = w.kind === "mail";
              const isInquiry = w.kind === "inquiry";
              const iconWrap = isInquiry ? "bg-[#27BEAA]/15 text-[#27BEAA]" : isReply ? "bg-primary/20 text-primary"
                : isConsent ? "bg-amber-500/15 text-amber-400"
                : isMail ? "bg-emerald-500/15 text-emerald-500" : "bg-blue-500/15 text-blue-400";
              return (
                <button
                  key={w.id}
                  onClick={() => open(w)}
                  data-testid={`notification-item-${w.id}`}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-accent transition-colors border-b border-border/50 last:border-0"
                >
                  <span className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${iconWrap}`}>
                    {isInquiry ? <Mail className="h-4 w-4" /> : isReply ? <MessageSquare className="h-4 w-4" /> : isConsent ? <Clock className="h-4 w-4" /> : isMail ? <Truck className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{w.title || (isReply ? "Lead replied" : isConsent ? "Awaiting consent" : isMail ? "Mailer update" : "Cadence call")}</div>
                    {w.description && <div className="text-xs text-muted-foreground truncate">{w.description}</div>}
                    {isInquiry && <div className="text-[10px] uppercase tracking-wide text-[#27BEAA] mt-0.5" data-testid={`notification-inquiry-${w.id}`}>Website inquiry — respond</div>}
                    {isReply && <div className="text-[10px] uppercase tracking-wide text-primary mt-0.5">New reply — respond</div>}
                    {isConsent && <div className="text-[10px] uppercase tracking-wide text-amber-400 mt-0.5">Awaiting consent — follow up</div>}
                    {isMail && <div className="text-[10px] uppercase tracking-wide text-emerald-500 mt-0.5">Mail delivered — follow up</div>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
